import { ref, readonly } from "vue";
import { CACHE_TTL, buildCacheKey } from "@/config/cache";

export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
}

const globalCacheStore = new Map<string, CacheEntry>();
const inFlightRequests = new Map<string, Promise<any>>();

export function getCachedEntry<T = any>(key: string): CacheEntry<T> | undefined {
  return globalCacheStore.get(key) as CacheEntry<T> | undefined;
}

export function setCachedEntry<T = any>(key: string, data: T, ttl: number = CACHE_TTL.default): void {
  globalCacheStore.set(key, {
    data,
    timestamp: Date.now(),
    ttl,
  });
}

export function isEntryFresh(entry: CacheEntry): boolean {
  if (!entry) return false;
  return Date.now() - entry.timestamp < entry.ttl;
}

export function invalidateCacheKey(keyOrPrefix: string): void {
  for (const key of globalCacheStore.keys()) {
    if (key === keyOrPrefix || key.startsWith(`${keyOrPrefix}:`)) {
      globalCacheStore.delete(key);
    }
  }
}

export function clearAllModuleCaches(): void {
  globalCacheStore.clear();
  inFlightRequests.clear();
}

export interface FetchResourceOptions {
  ttl?: number;
  forceRefresh?: boolean;
  silent?: boolean;
}

export function useCachedResource<T = any>(cacheKeyInput: string, defaultTtl: number = CACHE_TTL.default) {
  const data = ref<T | null>(null);
  const loading = ref<boolean>(false);
  const refreshing = ref<boolean>(false);
  const error = ref<Error | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  async function load(
    fetcher: () => Promise<T>,
    options: FetchResourceOptions = {}
  ): Promise<T | null> {
    const key = cacheKeyInput;
    const ttl = options.ttl ?? defaultTtl;
    const existing = getCachedEntry<T>(key);

    if (existing && !options.forceRefresh) {
      data.value = existing.data;
      lastFetchedAt.value = existing.timestamp;

      if (isEntryFresh(existing)) {
        loading.value = false;
        refreshing.value = false;
        return existing.data;
      }

      // Cache exists but is stale -> Background refresh
      loading.value = false;
      refreshing.value = true;
      executeFetch(key, fetcher, ttl, true);
      return existing.data;
    }

    // No cache exists or forced refresh -> Show primary loader
    if (!existing) {
      loading.value = true;
    } else {
      refreshing.value = true;
    }

    return executeFetch(key, fetcher, ttl, false);
  }

  async function executeFetch(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number,
    isBackground: boolean
  ): Promise<T | null> {
    let pending = inFlightRequests.get(key);

    if (!pending) {
      pending = (async () => {
        try {
          const freshData = await fetcher();
          setCachedEntry(key, freshData, ttl);
          return freshData;
        } finally {
          inFlightRequests.delete(key);
        }
      })();
      inFlightRequests.set(key, pending);
    }

    try {
      const result = await pending;
      data.value = result;
      lastFetchedAt.value = Date.now();
      error.value = null;
      return result;
    } catch (err: any) {
      console.warn(`[CachedResource] Fetch failed for key: ${key}`, err);
      if (!isBackground) {
        error.value = err instanceof Error ? err : new Error(String(err));
      }
      return data.value;
    } finally {
      loading.value = false;
      refreshing.value = false;
    }
  }

  function mutateLocal(newData: T): void {
    data.value = newData;
    setCachedEntry(cacheKeyInput, newData, defaultTtl);
  }

  function invalidate(): void {
    invalidateCacheKey(cacheKeyInput);
  }

  return {
    data,
    loading: readonly(loading),
    refreshing: readonly(refreshing),
    error: readonly(error),
    lastFetchedAt: readonly(lastFetchedAt),
    load,
    mutateLocal,
    invalidate,
    buildCacheKey,
  };
}
